import { useAxios } from '@monofront/vue-axios-plugin'
import { ref, InjectionKey, DeepReadonly } from 'vue'
import { UnwrapNestedRefs } from 'node_modules/@vue/reactivity/dist/reactivity'
import { LocationQuery, RouteLocationNormalized } from 'vue-router'

import { endpointDefault, getApiUrl } from '@/config/app'
import { getLanguageDefault } from '@/config/languages'
import { segments } from '@/config/routes'

import type { Request, Response } from 'express'

import {
  useResourceStore,
  Content,
  ResourceSingle,
  ResourceError,
} from '@/stores/resource'
import { logger } from '@/utils/logger'
import Cookies from 'js-cookie'

import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosResponseHeaders,
} from '@monofront/vue-axios-plugin'

import type { Language } from '@/config/languages'

export const ContentKey: InjectionKey<DeepReadonly<UnwrapNestedRefs<Content>>> =
  Symbol('Content')
export const contentDefault: Content = {
  title: 'Default title',
  colors: {
    foreground: '#000',
    background: '#000',
  },
}
export const contentRef = ref<Content>(contentDefault)

const buildQuery = (query: LocationQuery) => {
  const params = new URLSearchParams()
  Object.entries(query).forEach(([key, value]) => {
    params.append(key, value as string)
  })

  return `?${params.toString()}`
}

// Manage HTML lang attribute…
const setHtmlLang = (res: ResourceSingle, lang: Language) => {
  const resource = typeof res === 'string' ? ({} as ResourceSingle) : res

  if (!resource.head) {
    resource.head = {}
  }

  if (resource.head.htmlAttrs) {
    resource.head.htmlAttrs.lang = lang
  } else {
    resource.head.htmlAttrs = { lang }
  }

  return resource
}

/**
 * Parse route to build an API request like ${api}/${endpoint}/${resource}.
 */
export const parseRoute = (to: RouteLocationNormalized) => {
  const { path, meta, name, params, query } = to
  const api = meta.api || getApiUrl()
  let endpoint = endpointDefault
  let resource = path

  // REVIEW: purpose???
  // const allowedParams = {
  //   news: ['categories', 'categories[]'],
  // }

  if (params.lang) {
    // Remove lang from path
    resource = resource.replace(new RegExp(`^/${params.lang}`), '')
  }

  if (meta.endpoint) {
    // Set API endpoint
    ;({ endpoint } = meta)
    // Remove endpoint from resource path
    // Check for a named route params or a registered segment
    const segment = segments[endpoint]?.[params.lang as Language]
    /* eslint-disable indent */
    const pattern =
      params.endpoint || segment ? `(${endpoint}|${segment})` : endpoint
    /* eslint-enable indent */
    resource = resource.replace(new RegExp(`^/${pattern}`), '')
  }

  if (query.preview === 'true') {
    // Manage preview
    const {
      page_id: pageId,
      preview_id: previewId,
      id: postId,
      p: postType,
    } = query

    if (pageId || previewId) {
      resource = `/${pageId || previewId}`
    }

    if (postType && postId) {
      endpoint = postType as string
      resource = `/${postId}`
    }
  }

  // if (query['categories[]']) {
  //   const categories: string[] = Array.isArray(query['categories[]'])
  //     ? (query['categories[]'] as string[])
  //     : [query['categories[]']]
  //   resource += `?categories[]=${categories.join('&categories[]=')}`
  // }

  if (name === '404') {
    resource = 'supercalifragilisticexpialidocious'
  }

  resource = resource.replace(/^\//, '')

  return {
    api,
    endpoint,
    resource,
  }
}

export async function fetchResource(
  to: RouteLocationNormalized,
  request?: Request,
  serverRes?: Response
): Promise<
  [ResourceSingle | null, ResourceError | null, AxiosResponseHeaders]
> {
  const axios = useAxios()
  const languageDefault = getLanguageDefault()
  const config: AxiosRequestConfig = {}
  const { meta, params, query } = to
  const lang = (params.lang as Language) || languageDefault

  // No fetch for static routes
  if (meta.static) {
    return [
      {
        content: {},
        languages: {},
      } as ResourceSingle,
      null,
      {} as AxiosResponseHeaders,
    ]
  }

  const { api, endpoint, resource } = parseRoute(to)

  if (query && query.preview === 'true') {
    // Preview header
    config.headers = {
      'x-preview': 'true',
    }
  }

  config.headers = {
    ...(config.headers || {}),
    'x-light-mode': Cookies.get('light_mode') || '',
  }

  let url = `${api}/${endpoint}/${resource}`

  if (Object.keys(query).length > 0) {
    url += buildQuery(query)
  }

  logger.info('[fetchResource] fetching…', url)

  try {
    const response = (await axios.get(
      url,
      config
    )) as AxiosResponse<ResourceSingle>

    logger.info(
      '[fetchResource] fetched !',
      response.request.responseURL || response.request.res.responseUrl // client vs SSR
    )

    // Nested, so what?
    // if (params.nested || meta.nested) {}
    const resource = setHtmlLang(response.data as ResourceSingle, lang)

    return [resource, null, response.headers as AxiosResponseHeaders]
  } catch (error) {
    const { isAxiosError } = error as AxiosError

    if (isAxiosError) {
      const { response } = error as AxiosError

      if (response && response.status === 404) {
        const resource = setHtmlLang(response.data as ResourceSingle, lang)

        return [
          resource,
          error as AxiosError,
          response.headers as AxiosResponseHeaders,
        ]
      }
    }

    logger.error(
      '[fetchResource]',
      (error as AxiosError).code || (error as Error).message
    )

    return [
      null,
      isAxiosError ? (error as AxiosError) : (error as Error),
      {} as AxiosResponseHeaders,
    ]
  }
}

/**
 * Fetch resource from API directly
 * without being related on routing.
 */
export function useAPI() {
  const axios = useAxios()

  const fetchAPI = async (to: RouteLocationNormalized) => {
    try {
      const { query } = to
      const { api, endpoint, resource } = parseRoute(to)
      const url = `${api}/${endpoint}/${resource}${buildQuery(query)}`

      logger.info('[fetchAPI]', url)

      const response = (await axios.get(url)) as AxiosResponse<ResourceSingle>

      return response.data as ResourceSingle
    } catch (error) {
      logger.error('[fetchAPI]', error)

      return null
    }
  }

  return { fetchAPI }
}

// TODO: types
// export const updateResource = <T extends ResourceSingle | ResourceArchive>({
export const updateResource = (resource: ResourceSingle) => {
  const { content, id, head, languages, slug, template, type, url } = resource

  contentRef.value = content as Content

  // Use a function rather than object (`$patch(object)`)
  // to make a "clean" reset of props (persistent previous properties)
  useResourceStore().$patch(state => {
    state.content = content
    state.id = id
    state.head = head
    state.languages = languages
    state.slug = slug
    state.template = template
    state.type = type
    state.url = url
  })
}
